// src/components/admin/UsersTab.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Button, message, Tag, Dropdown, Menu, Modal } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, KeyOutlined, MoreOutlined } from '@ant-design/icons';
import { adminService } from '../../services/api';
import UserForm from './UserForm';
import { useAuth } from '../../context/AuthContext';
import ChangePasswordAdminModal from './ChangePasswordAdminModal';


// Se extrae la lógica del Dropdown a su propio componente.
// React.memo evitará que este componente se re-renderice si sus props no cambian.
const ActionMenu = React.memo(({ record, onEdit, onChangePassword, onDelete }) => {
    const menuItems = useMemo(() => {
        const items = [];
        if (onEdit) {
            items.push({
                key: 'edit',
                label: 'Editar Usuario',
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
            });
        }
        if (onChangePassword) {
            items.push({
                key: 'change-password',
                label: 'Cambiar Contraseña',
                icon: <KeyOutlined />,
                onClick: () => onChangePassword(record),
            });
        }
        if (onDelete) {
            items.push({
                key: 'delete',
                label: 'Eliminar Usuario',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
            });
        }
        return items;
    }, [record, onEdit, onChangePassword, onDelete]);

    if (menuItems.length === 0) return null;

    return (
        // --- INICIO: SOLUCIÓN DEFINITIVA ---
        // Se añade la prop 'getPopupContainer' para forzar al menú a renderizarse
        // relativo a su elemento padre inmediato, evitando conflictos de CSS globales.
        // Se revierte el 'trigger' a ['hover'] para restaurar la funcionalidad original.
        <Dropdown 
            menu={{ items: menuItems }} 
            trigger={['hover']}
            getPopupContainer={triggerNode => triggerNode.parentNode}
        >
            <Button icon={<MoreOutlined />} />
        </Dropdown>
        // --- FIN: SOLUCIÓN DEFINITIVA ---
    );
});

const UsersTab = () => {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] = useState(false);
    const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);

    const [modal, contextHolder] = Modal.useModal();

    const canCreateUser = user?.role?.profile?.permissions?.actions?.userManagement?.canCreateUser;
    const canEditUser = user?.role?.profile?.permissions?.actions?.userManagement?.canEditUser;
    const canDeleteUser = user?.role?.profile?.permissions?.actions?.userManagement?.canDeleteUser;
    const canChangeUserPassword = user?.role?.profile?.permissions?.actions?.userManagement?.canChangeUserPassword;

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminService.getUsers();
            if (data.success) {
                setUsers(data.data);
            }
        } catch (error) {
            console.error('Fallo al cargar usuarios:', error);
            message.error('No se pudieron cargar los usuarios.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleCreate = useCallback(() => {
        setEditingUser(null);
        setIsModalOpen(true);
    }, []);

    const handleEdit = useCallback((userToEdit) => {
        setEditingUser(userToEdit);
        setIsModalOpen(true);
    }, []);

    const handleDeleteRequest = useCallback((userToDelete) => {
        modal.confirm({
            title: `¿Está seguro de eliminar al usuario "${userToDelete.username}"?`,
            content: 'Esta acción no se puede deshacer.',
            okText: "Sí, Eliminar",
            okType: 'danger',
            cancelText: "No",
            onOk: async () => {
                try {
                    await adminService.deleteUser(userToDelete._id);
                    message.success('Usuario eliminado correctamente.');
                    fetchUsers();
                } catch (error) {
                    console.error('Fallo al eliminar usuario:', error);
                    message.error(error.response?.data?.message || 'Fallo al eliminar el usuario.');
                }
            },
        });
    }, [modal, fetchUsers]);

    const handleFormFinish = useCallback(async (values) => {
        setIsSubmitting(true);
        try {
            if (editingUser) {
                await adminService.updateUser(editingUser._id, values);
                message.success('Usuario actualizado correctamente.');
            } else {
                await adminService.createUser(values);
                message.success('Usuario creado correctamente.');
            }
            setIsModalOpen(false);
            fetchUsers();
        } catch (error) {
            console.error('Fallo en el formulario de usuario:', error);
            const errorMessage = error.response?.data?.message || 'Ocurrió un error al guardar el usuario.';
            message.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    }, [editingUser, fetchUsers]);

    const handleOpenChangePasswordModal = useCallback((userToEdit) => {
        setSelectedUserForPassword(userToEdit);
        setIsChangePasswordModalOpen(true);
    }, []);

    const handleCloseChangePasswordModal = useCallback(() => {
        setIsChangePasswordModalOpen(false);
        setSelectedUserForPassword(null);
    }, []);

    const handleChangePasswordFinish = useCallback(async (userId, values) => {
        setIsSubmitting(true);
        try {
            const response = await adminService.adminChangePassword(userId, values);
            message.success(response.data.message);
            handleCloseChangePasswordModal();
        } catch (error) {
            console.error('Fallo al cambiar contraseña:', error);
            message.error(error.response?.data?.message || 'Fallo al cambiar la contraseña.');
        } finally {
            setIsSubmitting(false);
        }
    }, [handleCloseChangePasswordModal]);

    const columns = useMemo(() => [
        { title: 'Nombre de Usuario', dataIndex: 'username', key: 'username' },
        { title: 'Nombre Completo', dataIndex: 'fullName', key: 'fullName' },
        { title: 'Rol', dataIndex: 'role', key: 'role', render: role => role ? role.name : 'Sin rol' },
        { title: 'Estado', dataIndex: 'isActive', key: 'isActive', render: isActive => (<Tag color={isActive ? 'green' : 'red'}>{isActive ? 'Activo' : 'Inactivo'}</Tag>) },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <ActionMenu
                    record={record}
                    onEdit={canEditUser ? handleEdit : null}
                    onChangePassword={canChangeUserPassword ? handleOpenChangePasswordModal : null}
                    onDelete={canDeleteUser ? handleDeleteRequest : null}
                />
            ),
        },
    ], [canEditUser, canChangeUserPassword, canDeleteUser, handleEdit, handleOpenChangePasswordModal, handleDeleteRequest]);

    return (
        <div>
            {contextHolder}
            {canCreateUser && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginBottom: 16 }}>
                    Crear Usuario
                </Button>
            )}
            <Table columns={columns} dataSource={users} loading={loading} rowKey="_id" bordered />
            <UserForm
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onFinish={handleFormFinish}
                initialValues={editingUser}
                submitting={isSubmitting}
            />
            {selectedUserForPassword && (
                <ChangePasswordAdminModal
                    open={isChangePasswordModalOpen}
                    onCancel={handleCloseChangePasswordModal}
                    onFinish={handleChangePasswordFinish}
                    user={selectedUserForPassword}
                    submitting={isSubmitting}
                />
            )}
        </div>
    );
};

export default UsersTab;
